// Doc-type label map. Backend stores canonical English keys
// (e.g. "medical_report"); UI translates at render time.
// fr/de populated 2026-07-23 for ARGOS Test Run 7-22 — French UI was
// still rendering all extracted document type labels in English.
const LABELS = {
  en: {
    medical_report: 'Medical Report',
    police_report: 'Police Report',
    damage_assessment: 'Damage Assessment',
    invoice: 'Invoice',
    receipt: 'Receipt',
    policy_document: 'Policy Document',
    correspondence: 'Correspondence',
    legal_notice: 'Legal Notice',
    witness_statement: 'Witness Statement',
    expert_opinion: 'Expert Opinion',
    photograph: 'Photograph',
    claim_form: 'Claim Form',
    settlement_agreement: 'Settlement Agreement',
    subrogation_notice: 'Subrogation Notice',
    other: 'Other Document',
  },
  fr: {
    medical_report: 'Rapport médical',
    police_report: 'Rapport de police',
    damage_assessment: 'Évaluation des dommages',
    invoice: 'Facture',
    receipt: 'Reçu',
    policy_document: 'Document de police',
    correspondence: 'Correspondance',
    legal_notice: 'Avis juridique',
    witness_statement: 'Déclaration de témoin',
    expert_opinion: 'Expertise',
    photograph: 'Photographie',
    claim_form: 'Formulaire de sinistre',
    settlement_agreement: 'Accord de règlement',
    subrogation_notice: 'Avis de subrogation',
    other: 'Autre document',
  },
  de: {
    medical_report: 'Ärztlicher Bericht',
    police_report: 'Polizeibericht',
    damage_assessment: 'Schadensbewertung',
    invoice: 'Rechnung',
    receipt: 'Quittung',
    policy_document: 'Policendokument',
    correspondence: 'Korrespondenz',
    legal_notice: 'Rechtliche Mitteilung',
    witness_statement: 'Zeugenaussage',
    expert_opinion: 'Gutachten',
    photograph: 'Fotografie',
    claim_form: 'Schadenformular',
    settlement_agreement: 'Vergleichsvereinbarung',
    subrogation_notice: 'Regressmitteilung',
    other: 'Sonstiges Dokument',
  },
};

export function docTypeLabel(typeKey, locale = 'en') {
  if (!typeKey) return '—';
  const table = LABELS[locale] || LABELS.en;
  const known = table[typeKey] || LABELS.en[typeKey];
  if (known) return known;
  // A tenant classifying documents this table has never heard of still gets
  // a label, not an identifier. The old fallback returned the raw key, so a
  // travel-assistance tenant's Documents screen read `flight_booking`,
  // `boarding_pass`, `luggage_claim` — snake_case internals shown to users.
  // Title-casing is not a translation, but it is honest and readable, and it
  // beats leaking the storage format.
  return String(typeKey)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase());
}

// AI-extracted field-key translations. Anthropic returns snake_case English
// keys (policy_number, insured_name, coverage_limit, …) that we render as
// labels on the Claim Details → Documents tab. Previously we only Title-
// Cased them, so a French reader saw "Policy Number" under a French UI
// (Test Run 7-22 report). This table covers the recurring keys; unknown
// keys fall through to the Title-Cased form.
const FIELD_LABELS = {
  en: {}, // Title-cased fallback is fine for English
  fr: {
    policy_number:      'Numéro de police',
    policy_no:          'Numéro de police',
    amount:             'Montant',
    total_amount:       'Montant total',
    insured_name:       "Nom de l'assuré",
    insured:            'Assuré',
    policyholder:       "Preneur d'assurance",
    coverage_start:     'Début de la couverture',
    coverage_end:       'Fin de la couverture',
    effective_date:     "Date d'effet",
    expiry_date:        "Date d'expiration",
    premium_amount:     'Montant de la prime',
    premium:            'Prime',
    coverage_limit:     'Limite de couverture',
    deductible:         'Franchise',
    line_of_business:   "Branche d'assurance",
    date_of_loss:       'Date du sinistre',
    claim_number:       'Numéro de sinistre',
    claim_no:           'Numéro de sinistre',
    broker:             'Courtier',
    broker_name:        'Nom du courtier',
    insurer:            'Assureur',
    currency:           'Devise',
    address:            'Adresse',
    city:               'Ville',
    postal_code:        'Code postal',
    canton:             'Canton',
    country:            'Pays',
    phone:              'Téléphone',
    email:              'E-mail',
    iban:               'IBAN',
  },
  de: {
    policy_number:      'Policennummer',
    policy_no:          'Policennummer',
    amount:             'Betrag',
    total_amount:       'Gesamtbetrag',
    insured_name:       'Name des Versicherten',
    insured:            'Versicherter',
    policyholder:       'Versicherungsnehmer',
    coverage_start:     'Deckungsbeginn',
    coverage_end:       'Deckungsende',
    effective_date:     'Wirksamkeitsdatum',
    expiry_date:        'Ablaufdatum',
    premium_amount:     'Prämienbetrag',
    premium:            'Prämie',
    coverage_limit:     'Deckungssumme',
    deductible:         'Selbstbehalt',
    line_of_business:   'Versicherungssparte',
    date_of_loss:       'Schadendatum',
    claim_number:       'Schadennummer',
    claim_no:           'Schadennummer',
    broker:             'Makler',
    broker_name:        'Maklername',
    insurer:            'Versicherer',
    currency:           'Währung',
    address:            'Adresse',
    city:               'Ort',
    postal_code:        'PLZ',
    canton:             'Kanton',
    country:            'Land',
    phone:              'Telefon',
    email:              'E-Mail',
    iban:               'IBAN',
  },
};

// camelCase or snake_case field key → "Human Readable Label".
// extractedFields uses arbitrary keys from the AI, so labels are derived not hardcoded.
// Optional `locale` argument: when the caller passes 'fr' or 'de', known
// keys are translated. Unknown keys still fall through to the Title-Cased
// English form so nothing renders blank.
export function humanizeFieldKey(key, locale = 'en') {
  if (!key) return '';
  const normalized = String(key).replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
  const table = FIELD_LABELS[locale];
  if (table && table[normalized]) return table[normalized];
  return String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, c => c.toUpperCase());
}
